import { useState, useEffect, useCallback } from "preact/hooks";
import { parseQueryParams, updateQueryParams } from "../lib/router";

export function useUrlStringState(
  key: string,
  defaultValue: string | null = null
): [string | null, (value: string | null) => void] {
  const [value, setValue] = useState<string | null>(() => {
    const params = parseQueryParams();
    return params[key] ?? defaultValue;
  });

  const setUrlValue = useCallback(
    (newValue: string | null) => {
      setValue(newValue);
      updateQueryParams({ [key]: newValue });
    },
    [key]
  );

  useEffect(() => {
    const handlePopState = () => {
      const params = parseQueryParams();
      setValue(params[key] ?? defaultValue);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [key, defaultValue]);

  return [value, setUrlValue];
}

export function useUrlArrayState(
  key: string,
  defaultValue: string[] = []
): [string[], (value: string[]) => void] {
  const [value, setValue] = useState<string[]>(() => {
    const params = parseQueryParams();
    const param = params[key];
    if (!param) return defaultValue;
    return param.split(",").filter(Boolean);
  });

  const setUrlValue = useCallback(
    (newValue: string[]) => {
      setValue(newValue);
      updateQueryParams({
        [key]: newValue.length > 0 ? newValue.join(",") : null,
      });
    },
    [key]
  );

  useEffect(() => {
    const handlePopState = () => {
      const params = parseQueryParams();
      const param = params[key];
      if (!param) {
        setValue(defaultValue);
      } else {
        setValue(param.split(",").filter(Boolean));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [key, defaultValue]);

  return [value, setUrlValue];
}
